import { api } from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";

/**
 * Service for option groups (Personnalisation des plats), usable for both
 * products and product variants (déclinaisons).
 *
 * Backed by the Clicoo "personnalisation-plats" API:
 *   GET    /{entityType}/{entityId}/option-groups
 *   POST   /{entityType}/{entityId}/option-groups
 *   PUT    /{entityType}/{entityId}/option-groups/{groupId}
 *   DELETE /{entityType}/{entityId}/option-groups/{groupId}   (soft-delete)
 * where entityType is "products" or "variants".
 *
 * CONFIG (create/update/delete) is reserved to Restaurant accounts
 * (busniessType === "004"); the backend returns 403 otherwise. Reading is
 * allowed for everyone (returns [] for entities without options).
 */

const withAccountHeaders = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    return {
        headers: {
            "X-account-id": account?.id,
            "Accept": "application/json",
        },
    };
};

const basePath = (entityType, entityId) => `/${entityType}/${entityId}/option-groups`;

export const listOptionGroups = async (entityType, entityId) => {
    const config = await withAccountHeaders();
    const response = await api.get(basePath(entityType, entityId), config);
    return response.data;
};

export const createOptionGroup = async (entityType, entityId, group) => {
    const config = await withAccountHeaders();
    const response = await api.post(
        basePath(entityType, entityId),
        serializeGroup(group),
        config
    );
    return response.data;
};

export const updateOptionGroup = async (entityType, entityId, groupId, group) => {
    const config = await withAccountHeaders();
    const response = await api.put(
        `${basePath(entityType, entityId)}/${groupId}`,
        serializeGroup(group),
        config
    );
    return response.data;
};

export const deleteOptionGroup = async (entityType, entityId, groupId) => {
    const config = await withAccountHeaders();
    await api.delete(`${basePath(entityType, entityId)}/${groupId}`, config);
};

/**
 * Map the UI group shape to the API payload (snake_case). When `options` is
 * provided on an update, the whole option set is REPLACED server-side.
 */
const serializeGroup = (group) => ({
    name: group.name,
    selection_type: group.selectionType || "single",
    required: !!group.required,
    // Free-quantity threshold only applies to multi-select groups: the first N
    // selected options are free, any beyond that are charged their price_delta.
    free_quantity: group.selectionType === "multiple" ? (Number(group.freeQuantity) || 0) : 0,
    sort_order: Number.isFinite(group.sortOrder) ? group.sortOrder : 0,
    options: (group.options || []).map((option, index) => ({
        name: option.name,
        price_delta: Number(option.priceDelta) || 0,
        sort_order: Number.isFinite(option.sortOrder) ? option.sortOrder : index,
        active: option.active !== false,
    })),
});
